import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { latToDms, lonToDms } from '@jupi/shared'
import { ConfirmDialog, RemoteContent, Title, formatDate, useSnackbar } from '@jupi/ui'
import { useDeleteRoute, useLoadRoute, useSavedRoute, useSavedRoutes, type SavedRouteSummary } from '../../api/plan'

// The member's saved routes. Tapping one opens its points, with the
// buttons to load it into the flight plan or delete it.
export default function RoutesPage() {
  const { t } = useTranslation()
  const { data: routes = [], isLoading, error } = useSavedRoutes()
  const [open, setOpen] = useState<number | null>(null)

  return (
    <Box>
      <Title label={t('nav.planRoutes')} />
      <RemoteContent isLoading={isLoading} error={error}>
        {routes.length === 0 ? (
          <Typography color="text.secondary">{t('plan.routes.none')}</Typography>
        ) : (
          <Stack spacing={1}>
            {routes.map((route) => (
              <RouteCard
                key={route.routeId}
                route={route}
                open={open === route.routeId}
                onToggle={() => setOpen(open === route.routeId ? null : route.routeId)}
              />
            ))}
          </Stack>
        )}
      </RemoteContent>
    </Box>
  )
}

function RouteCard({ route, open, onToggle }: { route: SavedRouteSummary; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardActionArea onClick={onToggle} aria-expanded={open}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {route.name}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                {route.summary}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('plan.routes.points', { count: route.pointCount })} ·{' '}
                {t('plan.routes.saved', { date: formatDate(route.updatedAt) })}
              </Typography>
            </Box>
            <Icon icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={24} />
          </Stack>
        </CardContent>
      </CardActionArea>
      <Collapse in={open} unmountOnExit>
        <RoutePoints routeId={route.routeId} />
        <RouteActions route={route} />
      </Collapse>
    </Card>
  )
}

function RoutePoints({ routeId }: { routeId: number }) {
  const { t } = useTranslation()
  const { data: route, isLoading } = useSavedRoute(routeId)
  if (isLoading || !route) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }
  return (
    <>
      <Divider />
      <Stack divider={<Divider />}>
        {route.points.map((point) => (
          <Stack key={point.position} direction="row" spacing={1.5} sx={{ px: 2, py: 1, alignItems: 'baseline' }}>
            <Typography variant="caption" color="text.secondary" sx={{ width: 16 }}>
              {point.position + 1}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, minWidth: 56 }}>
              {point.code}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {point.lat !== null && point.lon !== null
                ? `${latToDms(point.lat)} ${lonToDms(point.lon)}`
                : t('plan.routes.noPosition')}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </>
  )
}

function RouteActions({ route }: { route: SavedRouteSummary }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbar()
  const loadRoute = useLoadRoute()
  const deleteRoute = useDeleteRoute()
  const [confirm, setConfirm] = useState<'load' | 'delete' | null>(null)

  const act = async () => {
    const action = confirm
    setConfirm(null)
    try {
      if (action === 'load') {
        await loadRoute.mutateAsync(route.routeId)
        navigate('/plan')
      } else if (action === 'delete') {
        await deleteRoute.mutateAsync(route.routeId)
      }
    } catch {
      showSnackbar(t('common.error'), { severity: 'error' })
    }
  }

  return (
    <>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ p: 1.5, justifyContent: 'space-between' }}>
        <Button color="error" startIcon={<Icon icon="mdi:delete-outline" />} onClick={() => setConfirm('delete')}>
          {t('plan.routes.delete')}
        </Button>
        <Button variant="contained" startIcon={<Icon icon="mdi:upload-outline" />} onClick={() => setConfirm('load')}>
          {t('plan.routes.load')}
        </Button>
      </Stack>
      <ConfirmDialog
        open={confirm !== null}
        title={confirm === 'delete' ? t('plan.routes.delete') : t('plan.routes.load')}
        message={confirm === 'delete' ? t('plan.routes.deleteConfirm', { name: route.name }) : t('plan.routes.loadConfirm')}
        confirmLabel={confirm === 'delete' ? t('plan.routes.delete') : t('plan.routes.load')}
        cancelLabel={t('common.cancel')}
        destructive={confirm === 'delete'}
        onConfirm={() => void act()}
        onClose={() => setConfirm(null)}
      />
    </>
  )
}
